import React, { useState } from 'react';
import BookingDobPickerModal from './BookingDobPickerModal';
import type { BookingPassengerFormRow, BookingPassengerGender } from '../lib/bookingPassengers';
import {
  fieldErrorsForRow,
  formatDobDisplay,
  newBookingPassengerRow,
} from '../lib/bookingPassengers';

const MAX_PASSENGERS = 9;

export type BookingPassengerSectionProps = {
  rows: BookingPassengerFormRow[];
  onChange: (rows: BookingPassengerFormRow[]) => void;
  error: string | null;
  showFieldErrors?: boolean;
  readOnly?: boolean;
  /** Hide the default h2 (e.g. when embedded in payment with its own section title). */
  hideTitle?: boolean;
};

function updateRow(
  rows: BookingPassengerFormRow[],
  id: string,
  patch: Partial<BookingPassengerFormRow>,
): BookingPassengerFormRow[] {
  return rows.map((r) => (r.id === id ? { ...r, ...patch } : r));
}

function removeRow(rows: BookingPassengerFormRow[], id: string): BookingPassengerFormRow[] {
  const next = rows.filter((r) => r.id !== id);
  return next.length > 0 ? next : [newBookingPassengerRow()];
}

function DobField({
  row,
  readOnly,
  hasError,
  onChange,
}: {
  row: BookingPassengerFormRow;
  readOnly?: boolean;
  hasError?: boolean;
  onChange: (iso: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const display = formatDobDisplay(row.dateOfBirth);

  const openPicker = () => {
    if (readOnly) return;
    setOpen(true);
  };

  return (
    <div className={`booking-passenger-form__field${hasError ? ' is-error' : ''}`}>
      <label htmlFor={`${row.id}-dob-display`}>
        Date of Birth<span className="req">*</span>
      </label>
      <input
        id={`${row.id}-dob-display`}
        type="text"
        readOnly
        required
        placeholder="Date of Birth"
        value={display}
        className="booking-passenger-form__dob-display"
        aria-invalid={hasError || undefined}
        onClick={openPicker}
        onFocus={openPicker}
      />
      <BookingDobPickerModal
        open={open}
        valueIso={row.dateOfBirth}
        onClose={() => setOpen(false)}
        onConfirm={(iso) => onChange(iso)}
      />
    </div>
  );
}

function PassengerCard({
  row,
  index,
  total,
  onChange,
  onRemove,
  readOnly,
  showFieldErrors,
}: {
  row: BookingPassengerFormRow;
  index: number;
  total: number;
  onChange: (patch: Partial<BookingPassengerFormRow>) => void;
  onRemove: () => void;
  readOnly?: boolean;
  showFieldErrors?: boolean;
}) {
  const genderName = `gender-${row.id}`;
  const errs = showFieldErrors ? fieldErrorsForRow(row) : null;
  const canRemove = !readOnly && total > 1;

  return (
    <div className="booking-passenger-block" id={`passenger-${row.id}`}>
      <li className="booking-passenger-card">
        <div className="booking-passenger-card__head">
          <div className="booking-passenger-card__title">
            <span>Passenger</span>
          </div>
          {canRemove ? (
            <button type="button" className="booking-passenger-remove" onClick={onRemove}>
              Remove
            </button>
          ) : null}
        </div>
        <div className="booking-passenger-card__body">
          <div className="booking-passenger-form-grid">
            <div
              className={`booking-passenger-form__gender-block${errs?.gender ? ' is-error' : ''}`}
            >
              <fieldset className="booking-passenger-form__gender">
                <legend>
                  Gender<span className="req">*</span>
                </legend>
                <div
                  className="booking-passenger-form__radios"
                  role="radiogroup"
                  aria-label={`Gender passenger ${index + 1}`}
                  aria-invalid={errs?.gender || undefined}
                >
                  {(
                    [
                      ['male', 'Male'],
                      ['female', 'Female'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="booking-passenger-form__radio">
                      <input
                        type="radio"
                        name={genderName}
                        value={value}
                        checked={row.gender === value}
                        disabled={readOnly}
                        onChange={() => onChange({ gender: value as BookingPassengerGender })}
                      />
                      <span className="booking-passenger-form__radio-ui" aria-hidden />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
            <div className="booking-passenger-form__notice">
              The passenger names must match the travel documents.
            </div>
            <div className={`booking-passenger-form__field${errs?.lastName ? ' is-error' : ''}`}>
              <label htmlFor={`${row.id}-last`}>
                Last name<span className="req">*</span>
              </label>
              <input
                id={`${row.id}-last`}
                type="text"
                required
                placeholder="Last name"
                value={row.lastName}
                readOnly={readOnly}
                disabled={readOnly}
                aria-invalid={errs?.lastName || undefined}
                onChange={(e) => onChange({ lastName: e.target.value })}
                autoComplete={index === 0 ? 'family-name' : 'off'}
              />
            </div>
            <div className={`booking-passenger-form__field${errs?.firstName ? ' is-error' : ''}`}>
              <label htmlFor={`${row.id}-first`}>
                First name<span className="req">*</span>
              </label>
              <input
                id={`${row.id}-first`}
                type="text"
                required
                placeholder="First name"
                value={row.firstName}
                readOnly={readOnly}
                disabled={readOnly}
                aria-invalid={errs?.firstName || undefined}
                onChange={(e) => onChange({ firstName: e.target.value })}
                autoComplete={index === 0 ? 'given-name' : 'off'}
              />
            </div>
            <div className="booking-passenger-form__field">
              <label htmlFor={`${row.id}-middle`}>Middle name</label>
              <input
                id={`${row.id}-middle`}
                type="text"
                placeholder="Middle name"
                value={row.middleName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => onChange({ middleName: e.target.value })}
                autoComplete={index === 0 ? 'additional-name' : 'off'}
              />
            </div>
            <DobField
              row={row}
              readOnly={readOnly}
              hasError={errs?.dateOfBirth}
              onChange={(iso) => onChange({ dateOfBirth: iso })}
            />
          </div>
        </div>
      </li>
    </div>
  );
}

export default function BookingPassengerSection({
  rows,
  onChange,
  error,
  showFieldErrors,
  readOnly,
  hideTitle,
}: BookingPassengerSectionProps) {
  const canAdd = !readOnly && rows.length < MAX_PASSENGERS;

  return (
    <>
      {!hideTitle ? <h2 className="booking-section-title">Passenger details</h2> : null}
      {error ? (
        <p className="booking-passenger-error" role="alert">
          {error}
        </p>
      ) : null}
      <ul className="booking-passenger-list">
        {rows.map((row, index) => (
          <PassengerCard
            key={row.id}
            row={row}
            index={index}
            total={rows.length}
            readOnly={readOnly}
            showFieldErrors={showFieldErrors}
            onChange={(patch) => onChange(updateRow(rows, row.id, patch))}
            onRemove={() => onChange(removeRow(rows, row.id))}
          />
        ))}
      </ul>
      {!readOnly ? (
        <div className="booking-add-passenger-wrap">
          <button
            type="button"
            className="booking-add-passenger"
            disabled={!canAdd}
            onClick={() => onChange([...rows, newBookingPassengerRow()])}
          >
            <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden="true">
              <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            Add passenger <span className="booking-add-passenger__max">(Max: {MAX_PASSENGERS})</span>
          </button>
        </div>
      ) : null}
    </>
  );
}
